import React, { useEffect, useState } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import PaginationComponent from '../commonComponents/PaginationComponent';
import { url } from '../../util/config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};
const getToday = () => new Date().toISOString().slice(0, 10);

const SummaryReportComponent = () => {
    const [users, setUsers] = useState([]);
    const [senders, setSenders] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedSender, setSelectedSender] = useState('');
    const [startDate, setStartDate] = useState(getFirstDayOfMonth());
    const [endDate, setEndDate] = useState(getToday());
    const [reports, setReports] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isSendersLoading, setIsSendersLoading] = useState(false);

    // Fetch users
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${url}/users`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!response.ok) throw new Error();
                const data = await response.json();
                setUsers(data || []);
            } catch { toast.error('Failed to load users'); }
        };
        fetchUsers();
    }, []);

    // Fetch senders filtered by selected user
    useEffect(() => {
        const fetchSenders = async () => {
            setIsSendersLoading(true);
            setSelectedSender('');
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${url}/profiles`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!response.ok) throw new Error();
                const data = await response.json();
                const filtered = selectedUser
                    ? data.filter(p => p.owner === selectedUser || p.owner?._id === selectedUser)
                    : data;
                setSenders(filtered);
            } catch { setSenders([]); }
            finally { setIsSendersLoading(false); }
        };
        fetchSenders();
    }, [selectedUser]);

    // Fetch reports — FIX: ${query} not $(query)
    const fetchReports = async (page = 1) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const skip = (page - 1) * itemsPerPage;
            const query = `limit=${itemsPerPage}&skip=${skip}`;
            const body = { startdate: startDate, enddate: endDate };
            if (selectedUser) body.userid = selectedUser;
            if (selectedSender) body.sender = selectedSender;

            const response = await fetch(`${url}/summaryReport?${query}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(body),
            });
            if (!response.ok) throw new Error();
            const data = await response.json();
            const list = Array.isArray(data) ? data : (data.data || []);
            setReports(list);
            setTotalCount(data.totalCount || list.length || 0);
        } catch { toast.error('Failed to load report data'); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchReports(1); /* eslint-disable-next-line */ }, []);

    const handleSearch = () => { setCurrentPage(1); fetchReports(1); };
    const handlePageChange = (page) => { setCurrentPage(page); fetchReports(page); };

    const paginatedReports = reports.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const STATUS_COLS = ['Submitted', 'Delivered', 'Read', 'Failed'];
    const STATUS_COLORS = {
        Submitted:  '#1565c0',
        Delivered:  '#16a34a',
        Read:       '#7c3aed',
        Failed:     '#dc2626',
    };

    return (
        <WrapperComponent>
            <div style={styles.page}>
                {/* Page header */}
                <div style={styles.pageHeader}>
                    <div>
                        <h5 style={styles.pageTitle}>Summary Report</h5>
                        <p style={styles.pageSubtitle}>Campaign-level delivery summary</p>
                    </div>
                </div>

                {/* Filters */}
                <div style={styles.filterCard}>
                    <div style={styles.filterGrid}>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>User</label>
                            <select style={styles.filterSelect} value={selectedUser}
                                onChange={e => { setSelectedUser(e.target.value); setSelectedSender(''); }}>
                                <option value="">All Users</option>
                                {users.map(user => (
                                    <option key={user._id} value={user._id}>{user.name}</option>
                                ))}
                            </select>
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>Sender</label>
                            <select style={styles.filterSelect} value={selectedSender}
                                onChange={e => setSelectedSender(e.target.value)}
                                disabled={!selectedUser || isSendersLoading}>
                                <option value="">{isSendersLoading ? 'Loading…' : 'All Senders'}</option>
                                {senders.map((s, i) => (
                                    <option key={i} value={s.whatsapp_no}>{s.whatsapp_no}</option>
                                ))}
                            </select>
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>Start Date</label>
                            <input type="date" style={styles.filterInput} value={startDate}
                                max={endDate} onChange={e => setStartDate(e.target.value)} />
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>End Date</label>
                            <input type="date" style={styles.filterInput} value={endDate}
                                min={startDate} max={getToday()} onChange={e => setEndDate(e.target.value)} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                            <button style={styles.btnPrimary} onClick={handleSearch} disabled={loading}>
                                {loading
                                    ? <><span style={styles.spinner}></span> Loading…</>
                                    : <><i className="bi bi-search" style={{ marginRight: 6 }}></i>Search</>}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Table card */}
                <div style={styles.tableCard}>
                    <div style={styles.tableHeader}>
                        <h6 style={styles.tableTitle}>
                            <i className="bi bi-bar-chart-line" style={{ marginRight: 8, color: '#1565c0' }}></i>
                            Campaign Summary
                        </h6>
                        <span style={styles.resultCount}>{reports.length} campaign{reports.length !== 1 ? 's' : ''}</span>
                    </div>

                    {loading ? (
                        <div style={styles.centerState}>
                            <div className="spinner-border text-primary" style={{ width: '2rem', height: '2rem' }}></div>
                            <p style={styles.stateText}>Loading report data…</p>
                        </div>
                    ) : reports.length === 0 ? (
                        <div style={styles.centerState}>
                            <i className="bi bi-file-earmark-bar-graph" style={{ fontSize: 36, color: '#d1d5db' }}></i>
                            <p style={styles.stateText}>No campaigns found for the selected filters.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table style={styles.table}>
                                <thead>
                                    <tr style={styles.tableHead}>
                                        <th style={styles.th}>Sender</th>
                                        <th style={styles.th}>User</th>
                                        <th style={styles.th}>Campaign</th>
                                        <th style={styles.th}>Balance Used</th>
                                        <th style={styles.th}>Total</th>
                                        {STATUS_COLS.map(s => (
                                            <th key={s} style={{ ...styles.th, color: STATUS_COLORS[s] }}>{s}</th>
                                        ))}
                                        <th style={styles.th}>Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedReports.map((row, idx) => (
                                        <tr key={idx} style={styles.tr}>
                                            <td style={styles.td}>{row.sender || '—'}</td>
                                            <td style={styles.td}>{row.username || '—'}</td>
                                            <td style={styles.td}><strong>{row.campaign_name || '—'}</strong></td>
                                            <td style={styles.td}>{row.balance_used ?? '—'}</td>
                                            <td style={styles.td}><strong>{row.total_count ?? 0}</strong></td>
                                            {STATUS_COLS.map(s => (
                                                <td key={s} style={{ ...styles.td, color: STATUS_COLORS[s], fontWeight: '600' }}>
                                                    {row[s] !== undefined ? row[s] : 0}
                                                </td>
                                            ))}
                                            <td style={styles.td}>
                                                {row.datetime ? new Date(row.datetime).toLocaleString() : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {!loading && reports.length > itemsPerPage && (
                    <PaginationComponent
                        currentPage={currentPage}
                        totalPages={Math.ceil(reports.length / itemsPerPage)}
                        onPageChange={handlePageChange}
                    />
                )}

                <ToastContainer />
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </WrapperComponent>
    );
};

const styles = {
    page: { display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
    pageTitle: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' },
    pageSubtitle: { margin: '2px 0 0', fontSize: '13px', color: '#6b7280' },
    filterCard: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '16px 20px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    filterGrid: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' },
    filterGroup: { display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 140 },
    filterLabel: { fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' },
    filterSelect: { padding: '8px 12px', fontSize: '13px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', color: '#374151', background: 'white' },
    filterInput: { padding: '8px 12px', fontSize: '13px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', color: '#374151' },
    tableCard: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    tableHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f3f4f6' },
    tableTitle: { margin: 0, fontSize: '14px', fontWeight: '700', color: '#111827', display: 'flex', alignItems: 'center' },
    resultCount: { fontSize: '12px', color: '#6b7280', background: '#f3f4f6', padding: '3px 10px', borderRadius: '20px' },
    centerState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '60px 20px' },
    stateText: { fontSize: '14px', color: '#6b7280', margin: 0 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    tableHead: { background: '#eff6ff' },
    th: { padding: '12px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#1565c0', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '2px solid #bfdbfe', whiteSpace: 'nowrap' },
    tr: { borderBottom: '1px solid #f3f4f6' },
    td: { padding: '11px 14px', color: '#374151', verticalAlign: 'middle' },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', padding: '8px 16px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' },
    spinner: { width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite', marginRight: 6 },
};

export default SummaryReportComponent;
